import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useGoalDeleteMutation } from "web_myplann/Api/Goals/useGoalDeleteMutation";
import { useGoalStateMutation } from "web_myplann/Api/Goals/useGoalStateMutation";
import { GoalCompletionStateEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

export type GoalActionDialog = "none" | "complete" | "reactivate" | "delete";

type UseGoalActionsParams = {
	goalId: number;
	allowEditByClient: boolean;
	onBlocked: () => void;
};

export const useGoalActions = ({
	goalId,
	allowEditByClient,
	onBlocked,
}: UseGoalActionsParams) => {
	const { t } = useResource();
	const navigate = useNavigate();

	const [dialog, setDialog] = useState<GoalActionDialog>("none");
	const closeDialog = () => setDialog("none");

	const { mutate: changeState, isPending: isChangingState } =
		useGoalStateMutation();
	const { mutate: deleteGoal, isPending: isDeleting } = useGoalDeleteMutation();

	const openDialog = (next: Exclude<GoalActionDialog, "none">) => {
		if (!allowEditByClient) {
			onBlocked();
			return;
		}
		setDialog(next);
	};

	const changeStateTo = (
		state: GoalCompletionStateEnum,
		successToast: string,
	) =>
		changeState(
			{ id: goalId, state },
			{
				onSuccess: () => {
					closeDialog();
					toast.success(successToast);
				},
			},
		);

	return {
		dialog,
		openDialog,
		closeDialog,
		isChangingState,
		isDeleting,
		complete: () =>
			changeStateTo(
				GoalCompletionStateEnum.Completed,
				t(Resources.Goals.Detail.Actions.Complete.successToast),
			),
		reactivate: () =>
			changeStateTo(
				GoalCompletionStateEnum.NotCompleted,
				t(Resources.Goals.Detail.Actions.Reactivate.successToast),
			),
		remove: () =>
			deleteGoal(goalId, {
				onSuccess: () => {
					closeDialog();
					toast.success(t(Resources.Goals.Detail.Delete.successToast));
					navigate(Routing.Goals.GoalsList.path(), { replace: true });
				},
			}),
	};
};
